<template>
  <section v-if="employee">
    <div class="nav-arrows">
      <button :disabled="!hasPrevGlobal" @click="goPrev">← Previous</button>
      <button :disabled="!hasNextGlobal" @click="goNext">Next →</button>
    </div>
    <div v-if="bannerMsg" class="success-banner" role="status">
      {{ bannerMsg }}
    </div>
    <div class="container">
      <Card class="mb-3">
        <template #title>
          <div class="flex items-center gap-3">
            <span class="font-semibold text-lg">
              {{ employee.first_name }} {{ employee.last_name }}
            </span>
          </div>
        </template>
      </Card>

      <div v-if="hasEmployeeHashtags" class="profile-hashtags mb-3">
        <template
          v-for="tag in employeeHashtags"
          :key="tag.slug || tag.label"
        >
          <RouterLink
            v-if="tag.slug"
            :to="`/tags/${tag.slug}`"
            class="profile-hashtag is-link"
          >
            #{{ tag.label }}
          </RouterLink>
          <span v-else class="profile-hashtag">
            #{{ tag.label }}
          </span>
        </template>
      </div>

      <!-- Admin actions -->
      <div v-if="auth.isAdminRole" class="admin-action-bar">
        <router-link :to="`/employee/${employee.employee_id}/edit`">
          <Button icon="pi pi-pencil" label="تعديل" severity="secondary" size="small" />
        </router-link>
        <Button
          icon="pi pi-trash"
          label="حذف الملف"
          severity="danger"
          size="small"
          :loading="deleting"
          @click="confirmDelete"
        />
      </div>

      <!-- PhotoSwipe gallery -->
      <Galleria
        :value="galleryImages"
        :numVisible="5"
        :circular="true"
        :showItemNavigators="true"
        :showThumbnails="true"
        thumbnailsPosition="bottom"
        class="mb-4"
      >
        <template #item="{ item }">
          <img
            :src="item.itemImageSrc"
            :alt="item.alt"
            style="width: 100%; max-height: 400px; object-fit: contain; display: block; margin: 0 auto;"
          />
        </template>

        <template #thumbnail="{ item }">
          <img
            :src="item.thumbnailImageSrc"
            :alt="item.alt"
            style="display: block; width: 64px; height: 64px; object-fit: cover;"
          />
        </template>
      </Galleria>

      <!-- Info blocks -->
      <div class="row">
        <!-- Personal info -->
        <InfoListCard title="معلومات أساسية" :items="personalItems" />

        <!-- Work info -->
        <div class="grid">
          <div class="col-12 md:col-6">
            <InfoListCard title="جهة العمل" :items="workItems" />
          </div>
          <div class="col-12 md:col-6">
            <InfoListCard title="الاتصال الشخصي" :items="contactItems" />
          </div>
        </div>
      </div>

      <Card v-if="hasEmployeeHashtags" class="mt-4">
        <template #title>
          <span class="flex items-center gap-2">
            <i class="pi pi-hashtag" />
            <span>الوسوم المرتبطة</span>
          </span>
        </template>
        <template #content>
          <div class="hashtag-cloud">
            <span
              v-for="tag in employeeHashtags"
              :key="tag.slug || tag.label"
              class="tag-chip"
            >
              <RouterLink
                v-if="tag.slug"
                :to="`/tags/${tag.slug}`"
                class="tag-chip-link"
              >
                #{{ tag.label }}
              </RouterLink>
              <span v-else class="tag-chip-link is-static">
                #{{ tag.label }}
              </span>
            </span>
          </div>
        </template>
      </Card>

      <Card v-if="hasFacebookDisplayNames" class="mt-4">
        <template #title>
          <span class="flex items-center gap-2">
            <i class="pi pi-facebook" />
            <span>أسماء فيسبوك</span>
          </span>
        </template>
        <template #content>
          <ul class="fb-name-list">
            <li v-for="(name, idx) in facebookDisplayNames" :key="`fb-name-${idx}`">
              {{ name }}
            </li>
          </ul>
        </template>
      </Card>

      <!-- Notes timeline -->
      <Card class="mt-4">
        <template #title>
          <span class="flex items-center gap-2">
            <i class="pi pi-bookmark" />
            <span>دفتر الملاحظات</span>
            <Tag v-if="notesTimeline.length" :value="notesTimeline.length" severity="info" />
          </span>
        </template>
        <template #content>
          <div v-if="!notesTimeline.length" class="text-color-secondary italic">
            لا توجد ملاحظات مسجلة
          </div>
          <ul v-else class="notes-list">
            <li v-for="(note, idx) in notesTimeline" :key="`${idx}-${note.date ?? 'na'}`" class="note-item">
              <strong>{{ formatNoteDate(note.date) }}</strong>
              <p>{{ note.note }}</p>
              <div v-if="note.attachments?.length" class="note-attachments">
                <p class="text-color-secondary text-sm mt-2 mb-1">المرفقات:</p>
                <ul>
                  <li v-for="attachment in note.attachments" :key="attachment.attachment_id" class="note-attachment-row">
                    <a
                      class="text-primary hover:underline font-semibold"
                      target="_blank"
                      rel="noopener"
                      :href="attachmentDownloadUrl(attachment.attachment_id)"
                    >
                      {{ attachment.title || attachment.summary || 'ملف PDF' }}
                    </a>
                    <a
                      v-if="attachment.url"
                      class="text-primary text-xs block truncate"
                      target="_blank"
                      rel="noopener"
                      :href="attachment.url"
                    >
                      {{ attachment.url }}
                    </a>
                    <span v-if="attachment.summary" class="block text-xs text-color-secondary">
                      {{ attachment.summary }}
                    </span>
                    <button
                      v-if="auth.isAdminRole"
                      type="button"
                      class="attachment-refresh"
                      :disabled="refreshingAttachmentId === attachment.attachment_id"
                      @click="refreshAttachment(attachment.attachment_id)"
                    >
                      <i class="pi pi-refresh" />
                      <span>تحديث</span>
                    </button>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </template>
      </Card>
    </div>
    

    <EmployeeRelationshipsManager
      v-if="employee"
      :employeeId="employee.employee_id"
      readonly
      hideWhenEmpty
    />

    <!-- Facebook profiles (compact list) -->
    <Card v-if="showFacebookCard" class="mt-4">
      <template #title>
        <span class="flex items-center gap-2">
          <i class="pi pi-facebook" />
          <span>حسابات فيسبوك</span>
          <Tag v-if="fbProfiles.length" :value="fbProfiles.length" severity="info" />
        </span>
      </template>

      <template #content>
        <div v-if="fbLoading" class="text-color-secondary">...جار التحميل</div>
        <ul v-else class="list-none p-0 m-0">
          <li v-for="p in fbProfiles" :key="`${employee?.employee_id}-${p.profile_id ?? normalizedUrl(p.profile_url)}`">
            <a :href="normalizedUrl(p.profile_url)" target="_blank" rel="noopener" class="text-primary hover:underline">
              {{ normalizedUrl(p.profile_url) }}
            </a>
          </li>
          <li v-if="!fbProfiles.length" class="text-color-secondary italic">لا توجد روابط فيسبوك</li>
        </ul>
      </template>
    </Card>

    <Card v-if="showWikiCard" class="mt-4">
      <template #title>
        <span class="flex items-center gap-2">
          <i class="pi pi-book" />
          <span>مقالات ذات صلة في الويكي</span>
          <Tag v-if="wikiPages.length" :value="wikiPages.length" severity="info" />
        </span>
      </template>
      <template #content>
        <div v-if="wikiLoading" class="text-color-secondary">...جار التحميل</div>
        <ul v-else class="list-none p-0 m-0">
          <li v-for="page in wikiPages" :key="page.page_id" class="mb-2">
            <RouterLink :to="`/wiki/${page.slug}`" class="text-primary hover:underline">
              {{ page.title }}
            </RouterLink>
            <small class="text-color-secondary block">
              {{ page.event_date ? formatWikiDate(page.event_date) : 'بدون تاريخ' }}
            </small>
          </li>
          <li v-if="!wikiPages.length" class="text-color-secondary italic">لا توجد إشارات في الويكي</li>
        </ul>
      </template>
    </Card>

  </section>

  <p v-else>...جار التحميل</p>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount, watch, type Ref } from "vue"
import { useRoute, useRouter, isNavigationFailure, NavigationFailureType } from "vue-router"
import axios from "axios"
import PhotoSwipeLightbox from "photoswipe/lightbox"
import type { components } from '@/types/api'
import { refreshLinkAttachment } from '@/services/linkAttachments'

//import Avatar from 'primevue/avatar'
import Galleria from 'primevue/galleria'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Tag from "primevue/tag"


import "photoswipe/style.css"
import { useFlashStore } from '@/store/flash'
import { useAuthStore } from '@/store/auth'
import InfoListCard from '@/components/employee/InfoListCard.vue'
import EmployeeRelationshipsManager from '@/components/employee/EmployeeRelationshipsManager.vue'
import { useEmployeeSearchStore } from '@/store/employeeSearch'
import { fetchWikiPagesForEmployee, type WikiPageSummary } from '@/services/wiki'

type Employee = any
type Assoc = any
type GalleriaItem = { itemImageSrc: string; thumbnailImageSrc: string; alt?: string }
type FbProfile = { profile_id?: number; profile_url?: string | null }
type EmployeeHashtag = { label: string; slug?: string | null }

const employee = ref<Employee | null>(null)
const loading = ref<boolean>(false)

const router = useRouter()
const route = useRoute()
const flash = useFlashStore()
const auth = useAuthStore()

const bannerMsg = ref<string>("")
const galleryEl: Ref<HTMLElement | null> = ref(null)
const props = defineProps<{ employeeId?: number }>()
let lightbox: PhotoSwipeLightbox | null = null

const fbLoading = ref(false)
const fbProfiles = ref<FbProfile[]>([])
const wikiLoading = ref(false)
const wikiPages = ref<WikiPageSummary[]>([])
const deleting = ref(false)

// Employees navigation
const store = useEmployeeSearchStore()
const ids = computed(() => store.employeeIds)
const currentId = computed(() => Number(route.params.id))
const inPageIndex = computed(() => ids.value.indexOf(currentId.value))
const index = computed(() => ids.value.indexOf(currentId.value))
const prevId = computed(() => (index.value > 0 ? ids.value[index.value - 1] : null))
const nextId = computed(() =>
  index.value >= 0 && index.value < ids.value.length - 1 ? ids.value[index.value + 1] : null
)

console.log('Current employee_id from route:', route.params.employee_id)
console.log('Search results length:', store.results?.length)
console.log('IDs in results:', store.results?.map(e => e.employee_id))

const goTo = async (empId: number) => {
  // Prefer path-based; avoids relying on names
  const target = { path: `/employee/${empId}`, query: route.query }
  const targetFullPath = router.resolve(target).fullPath
  if (targetFullPath === route.fullPath) return

  try {
    await router.push(target)
  } catch (err: any) {
    // You already suppress duplicated nav globally.
    // This logs *real* errors (e.g., bad path) with the message.
    console.error('router.push error:', err?.message || err)
  }
}

const goNext = async () => {
  const idx = inPageIndex.value
  if (idx === -1) return // not in the current page; no nav
  // within current page?
  if (idx < ids.value.length - 1) {
    return goTo(ids.value[idx + 1])
  }
  // need next page
  const globalIdx = (store.page - 1) * store.limit + idx
  if (globalIdx >= store.total - 1) return // already at last overall
  await store.fetchPage(store.page + 1)
  if (store.results.length) {
    return goTo(store.results[0].employee_id)
  }
}

const goPrev = async () => {
  const idx = inPageIndex.value
  if (idx === -1) return
  // within current page?
  if (idx > 0) {
    return goTo(ids.value[idx - 1])
  }
  // need previous page
  if (store.page <= 1) return // already at first overall
  await store.fetchPage(store.page - 1)
  if (store.results.length) {
    return goTo(store.results[store.results.length - 1].employee_id)
  }
}

async function loadWikiMentions(employeeId: number) {
  if (!employeeId) {
    wikiPages.value = []
    return
  }
  wikiLoading.value = true
  try {
    wikiPages.value = await fetchWikiPagesForEmployee(employeeId)
  } catch (err) {
    console.error('Failed to load wiki mentions', err)
  } finally {
    wikiLoading.value = false
  }
}

watch(
  () => route.params.id,
  (val) => {
    const id = Number(val)
    if (Number.isFinite(id)) {
      loadWikiMentions(id)
    }
  },
  { immediate: true }
)

function formatWikiDate(value?: string | null) {
  if (!value) return 'بدون تاريخ'
  return new Date(value).toLocaleDateString('ar-EG')
}

function formatNoteDate(value?: string | null) {
  if (!value) return '—'
  const time = Date.parse(value)
  if (Number.isNaN(time)) return value
  return new Date(time).toLocaleDateString('ar-EG')
}

const attachmentDownloadUrl = (id: number) => `/api/link-attachments/${id}/download`

const hasPrevGlobal = computed(() => {
  if (inPageIndex.value === -1) return false
  const globalIdx = (store.page - 1) * store.limit + inPageIndex.value
  return globalIdx > 0
})
const hasNextGlobal = computed(() => {
  if (inPageIndex.value === -1) return false
  const globalIdx = (store.page - 1) * store.limit + inPageIndex.value
  return globalIdx < store.total - 1
})



// Formatting functions
const fmt = (v: unknown, dash = '—') =>
  v == null || v === '' ? dash : String(v)

const fullName = (e?: Employee | null) =>
  e ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() : ''

const normalizedUrl = (url?: string | null, username?: string | null, fbId?: string | number | null) => {
  if (url && /^https?:\/\//i.test(url)) return url
  if (url) return `https://facebook.com/${String(url).replace(/^\/+/, '')}`
  if (username) return `https://facebook.com/${String(username).replace(/^\/+/, '')}`
  if (fbId) return `https://facebook.com/profile.php?id=${fbId}`
  return ''
}

const personalItems = computed(() => [
  { label: 'الإسم', value: fullName(employee.value) || '—' },
  { label: 'تاريخ الميلاد', value: fmt(employee.value?.birthday) },
  { label: 'النوع', value: fmt(employee.value?.gender) },
  { label: 'الحاله الإجتماعيه', value: fmt(employee.value?.merital_status) },
  { label: 'التعليم', value: fmt(employee.value?.school) },
])

const workItems = computed(() => [
  { label: 'جهة العمل', value: fmt(primaryCompany.value?.company_name) },
  { label: 'الرتبة', value: fmt(primaryAssoc.value?.rank) },
  { label: 'جهة التخصص', value: fmt(primaryAssoc.value?.department) },
  { label: 'المنصب', value: fmt(primaryAssoc.value?.title) },
  { label: 'محل العمل', value: fmt(primaryAssoc.value?.office) },
  { label: 'إيميل العمل', value: fmt(primaryAssoc.value?.office_email) },
  { label: 'هاتف العمل', value: fmt(primaryAssoc.value?.office_phone) },
])

const contactItems = computed(() => [
  { label: 'عنوان المنزل', value: fmt(employee.value?.address) },
  { label: 'المكان', value: `${fmt(employee.value?.city)}، ${fmt(employee.value?.state)}` },
  { label: 'البلد', value: fmt(employee.value?.country) },
  { label: 'الرقم البريدي', value: fmt(employee.value?.postal_code) },
  { label: 'الإيميل الشخصي', value: fmt(employee.value?.personal_email) },
  { label: 'رقم الجوال', value: fmt(employee.value?.mobile) },
  { label: 'هاتف المنزل', value: fmt(employee.value?.phone) },
])

const galleryImages = computed<GalleriaItem[]>(() =>
  (employee.value?.images ?? []).map((img: number) => ({
    itemImageSrc: imageUrl(img),
    thumbnailImageSrc: imageUrl(img),
    alt: 'Employee photo',
  }))
)

const notesTimeline = computed(() => {
  if (primaryAssoc.value?.notes?.length) {
    return primaryAssoc.value.notes
  }
  return employee.value?.notes ?? []
})

const facebookDisplayNames = computed<string[]>(() => employee.value?.facebook_names ?? [])
const hasFacebookDisplayNames = computed(() => facebookDisplayNames.value.length > 0)
const employeeHashtags = computed<EmployeeHashtag[]>(() => employee.value?.hashtags ?? [])
const hasEmployeeHashtags = computed(() => employeeHashtags.value.length > 0)
const showFacebookCard = computed(() => fbProfiles.value.length > 0)
const showWikiCard = computed(() => wikiPages.value.length > 0)
const refreshingAttachmentId = ref<number | null>(null)

function imageUrl(imageId: number) {
  const empId = currentId.value
  return `/api/employees/${empId}/images/${imageId}.png`
}

function showBannerFromFlash() {
  if (flash.message) {
    bannerMsg.value = flash.message
    setTimeout(() => { bannerMsg.value = ''; flash.clear() }, 3000)
  }
}

async function loadFacebookProfiles(empId: number) {
  fbLoading.value = true
  try {
    const { data } = await axios.get(`/api/employees/${empId}/facebook-profiles`)
    // Keep only entries that have a URL
    fbProfiles.value = (data ?? []).filter((p: any) => !!p?.profile_url)
  } finally {
    fbLoading.value = false
  }
}

function showBannerFromQuery() {
  const msg = route.query.msg
  if (msg) {
    bannerMsg.value = String(msg)
    setTimeout(() => { bannerMsg.value = "" }, 3000)
    const { msg: _m, success: _s, ...rest } = route.query
    router.replace({ path: route.path as string, query: rest })
  }
}

async function loadEmployee(id: number | string) {
  const empId = Number(id)
  const { data } = await axios.get(`/api/employees/${empId}/details`)
  employee.value = data as Employee
}

async function refreshAttachment(attachmentId: number) {
  if (!auth.isAdminRole) return
  refreshingAttachmentId.value = attachmentId
  try {
    await refreshLinkAttachment(attachmentId)
    if (employee.value?.employee_id) {
      await loadEmployee(employee.value.employee_id)
    }
    bannerMsg.value = "تم تحديث المرفق"
    setTimeout(() => { bannerMsg.value = "" }, 3000)
  } catch (err: any) {
    bannerMsg.value = err?.response?.data?.detail || "تعذر تحديث المرفق"
    setTimeout(() => { bannerMsg.value = "" }, 3000)
  } finally {
    refreshingAttachmentId.value = null
  }
}

async function confirmDelete() {
  if (!auth.isAdminRole || !employee.value) return
  const fullName = `${employee.value.first_name ?? ''} ${employee.value.last_name ?? ''}`.trim() || `#${employee.value.employee_id}`
  if (!window.confirm(`هل أنت متأكد من حذف ملف ${fullName}؟`)) return
  deleting.value = true
  try {
    await axios.delete(`/api/employees/${employee.value.employee_id}`)
    flash.set('تم حذف ملف الموظف بنجاح')
    router.replace('/employee/all')
  } catch (err: any) {
    bannerMsg.value = err?.response?.data?.detail || 'تعذر حذف الملف'
  } finally {
    deleting.value = false
  }
}

function initLightbox() {
  if (!galleryEl.value) return
  if (lightbox) { lightbox.destroy(); lightbox = null }
  lightbox = new PhotoSwipeLightbox({
    gallery: galleryEl.value,
    children: "a",
    pswpModule: () => import("photoswipe"),
  })
  lightbox.init()
}

onMounted(async () => {
  const empId = Number(route.params.id as string)
  try {
    await loadEmployee(empId)
    loadFacebookProfiles(empId)
    initLightbox()
  } catch (err) {
    console.error("Error loading employee", err)
  } finally {
    showBannerFromFlash()
    showBannerFromQuery()
  }
})

watch(() => route.params.id, async (newId, oldId) => {
  if (newId === oldId) return
  try {
    fbLoading.value = true
    fbProfiles.value = []  
    await loadEmployee(newId as string)
    await loadFacebookProfiles(Number(newId))
    initLightbox()
  } catch (e) {
    console.error("Error reloading employee", e)
  } finally {
    fbLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (lightbox) { lightbox.destroy(); lightbox = null }
})

// Derived info (first association as "primary")
const primaryAssoc = computed<Assoc | null>(() => employee.value?.companies?.[0] ?? null)
const primaryCompany = computed(() => primaryAssoc.value?.company_id ? primaryAssoc.value : null)

// Optional: load FB profiles separately if you need them here
</script>

<style scoped>
.my-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: start;
}
.my-gallery figure {
  width: 60px;
  height: 60px;
  margin: 0;
}
.profile-pic {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 1rem;
}
.my-gallery img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.info-title {
  font-weight: bold;
  margin-bottom: 0.5rem;
}
.success-banner {
  background: #e7f6ed;
  color: #0f5132;
  border: 1px solid #badbcc;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 8px 0 12px;
}
/* in <style scoped> */
.avatar-wrap {
  display: flex;
  justify-content: center;
}

/* neutralize legacy floats/sizing inside this block */
.avatar-wrap :deep(img) {
  float: none !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
}

.notes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.note-item {
  border-bottom: 1px solid var(--surface-border, #e2e8f0);
  padding-bottom: 0.5rem;
}

.note-item:last-child {
  border-bottom: none;
}

.note-item strong {
  display: block;
  color: var(--text-color-secondary, #475569);
  margin-bottom: 0.2rem;
}

.note-attachments ul {
  list-style: disc;
  margin: 0 0 0 1.25rem;
  padding: 0;
}

.note-attachments li {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.note-attachment-row {
  position: relative;
}

.attachment-refresh {
  margin-top: 0.35rem;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 999px;
  border: 1px solid #cbd5f5;
  background: #eef2ff;
  color: #2563eb;
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
}

.attachment-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hashtag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.profile-hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0;
}

.profile-hashtag {
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #030712;
  font-weight: 500;
  border: 1px solid transparent;
  font-size: 0.9rem;
}

.profile-hashtag.is-link {
  color: #2563eb;
  border-color: #c7d2fe;
}

.tag-chip {
  display: inline-flex;
}

.tag-chip-link {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.75rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: var(--text-color, #0f172a);
  text-decoration: none;
  border: 1px solid transparent;
  transition: background 0.2s ease;
}

.tag-chip-link:hover {
  background: #e2e8f0;
}

.tag-chip-link.is-static {
  cursor: default;
  opacity: 0.75;
}

.fb-name-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.admin-action-bar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

</style>
